import * as THREE from 'three';
import {
  createDimension,
  DIM_AXIS,
  DIM_AXIS_TO_LOCAL,
  DIM_GROUP,
  DIM_TYPE,
  DEFAULT_OFFSET_M,
} from './dimensionTypes';
import {
  buildAnchorKey,
  getLocalFrameBox,
  localAxisWorldDir,
  localDirToWorld,
  resolveAnchorWorld,
  worldDirToLocal,
} from './AnchorResolver';
import {
  chooseAutoPlacements,
  HIDDEN_SIDE_PENALTY,
  pushesAway,
  scoreCandidate,
  silhouetteOfMeshes,
} from './DimensionPlacement';
import { projectToScreen } from './ProjectionEngine';
import { boxForAnchor, getPartBox, moduleKindOf, PART } from './PartResolver';

/** Nhóm hiển thị (preset) mặc định cho DIM tự động của một module. */
const GROUP_BY_KIND = {
  upper: DIM_GROUP.UPPER,
  lower: DIM_GROUP.LOWER,
  overall: DIM_GROUP.OVERALL,
};

/**
 * Tạo 3 DIM rộng / cao / sâu cho một module.
 *
 * Nếu có `camera` + `viewport`, mỗi DIM được đặt theo hình chiếu trên màn hình:
 * chọn cạnh và hướng đẩy ra để đường DIM nằm ngoài thân module, không bị co
 * ngắn, nằm trong khung nhìn (xem DimensionPlacement). Nếu không, dùng cách
 * đơn giản theo `viewPoint` (phía gần camera nhất).
 * Vị trí được chọn một lần lúc tạo rồi lưu cố định, không nhảy khi xoay camera.
 *
 * @param {THREE.Group} group - group module (có userData.instanceId)
 * @param {string} instanceId
 * @param {{viewPoint?: THREE.Vector3, camera?: THREE.Camera,
 *          viewport?: {width: number, height: number},
 *          obstacles?: THREE.Group[],
 *          offsetM?: number, box?: THREE.Box3}} opts
 */
export const createAutoDimensions = (group, instanceId, opts = {}) => {
  const { viewPoint, offsetM = DEFAULT_OFFSET_M } = opts;
  const box = opts.box ?? getLocalFrameBox(group);
  if (!group || !instanceId || !box) return [];
  // Nhóm hiển thị theo loại module (tủ trên / tủ dưới / khác)
  const dimGroup = opts.group ?? GROUP_BY_KIND[moduleKindOf(group)];

  const { camera, viewport } = opts;
  if (camera && viewport) {
    const placements = chooseAutoPlacements(
      group,
      box,
      camera,
      viewport.width,
      viewport.height,
      { obstacles: opts.obstacles }
    );
    if (placements?.every(Boolean)) {
      return placements.map(pl =>
        createDimension({
          group: dimGroup,
          autoFor: instanceId,
          axis: pl.axis,
          anchors: pl.anchors.map(a => ({ instanceId, key: a.key })),
          offsetAxis: pl.offsetAxis,
          offsetSign: pl.offsetSign,
          offsetM: pl.offsetM,
        })
      );
    }
  }

  const center = box.getCenter(new THREE.Vector3());
  const near = { x: 'max', y: 'max', z: 'max' };
  if (viewPoint) {
    group.updateWorldMatrix(true, false);
    const viewLocal = group.worldToLocal(viewPoint.clone());
    ['x', 'y', 'z'].forEach(axis => {
      near[axis] = viewLocal[axis] < center[axis] ? 'min' : 'max';
    });
  }
  const sign = side => (side === 'max' ? 1 : -1);
  const anchor = (x, y, z) => ({
    instanceId,
    key: buildAnchorKey({ x, y, z }),
  });
  const common = { group: dimGroup, offsetM, autoFor: instanceId };

  return [
    // Rộng (trục Z): cạnh trên, phía gần camera, đẩy lên trên
    createDimension({
      ...common,
      axis: DIM_AXIS.WIDTH,
      anchors: [anchor(near.x, 'max', 'min'), anchor(near.x, 'max', 'max')],
      offsetAxis: 'y',
      offsetSign: 1,
    }),
    // Cao (trục Y): cạnh đứng ở góc gần camera, đẩy ra theo X
    createDimension({
      ...common,
      axis: DIM_AXIS.HEIGHT,
      anchors: [anchor(near.x, 'min', near.z), anchor(near.x, 'max', near.z)],
      offsetAxis: 'x',
      offsetSign: sign(near.x),
    }),
    // Sâu (trục X): cạnh trên, phía gần camera, đẩy ra theo Z
    createDimension({
      ...common,
      axis: DIM_AXIS.DEPTH,
      anchors: [anchor('min', 'max', near.z), anchor('max', 'max', near.z)],
      offsetAxis: 'z',
      offsetSign: sign(near.z),
    }),
  ];
};

/**
 * Tạo một ghi chú kỹ thuật (annotation) gắn vào một điểm neo: mũi tên chỉ vào
 * điểm đó kèm khung chữ. Gắn vào module / bộ phận nên di chuyển theo mô hình.
 * `labelShift` là vị trí khung chữ (px) so với điểm neo trên màn hình.
 */
export const createAnnotation = ({
  anchor,
  text = 'Note',
  group = DIM_GROUP.ACCESSORY,
  labelShift = [60, -50],
}) =>
  createDimension({
    type: DIM_TYPE.ANNOTATION,
    anchors: [{ ...anchor }],
    text,
    group,
    labelShift,
  });

/**
 * Giải điểm neo của annotation ra toạ độ world.
 * @returns {null | {id: string, point: THREE.Vector3}} null nếu mất neo
 */
export const resolveAnnotation = (dim, getGroup, opts) => {
  const anchor = dim?.anchors?.[0];
  if (!anchor) return null;
  const group = getGroup(anchor.instanceId);
  if (!group) return null;
  const point = resolveAnchorWorld(
    group,
    boxForAnchor(group, anchor, opts),
    anchor
  );
  return point ? { id: dim.id, point } : null;
};

/** Các kích thước tự động của từng loại bộ phận. */
const PART_AXES = {
  // Dài (Z), sâu (X), dày (Y)
  [PART.COUNTERTOP]: [DIM_AXIS.WIDTH, DIM_AXIS.DEPTH, DIM_AXIS.HEIGHT],
  // Dài (Z), cao (Y); bề dày mỏng, không đo
  [PART.BACKSPLASH]: [DIM_AXIS.WIDTH, DIM_AXIS.HEIGHT],
};

/**
 * Tạo DIM tự động cho mặt đá hoặc ốp tường.
 *  - mặt đá: dài, sâu, dày
 *  - ốp tường: dài, cao
 * Neo vào module sở hữu + `part`, nên tìm lại được mesh mới mỗi khi bộ phận bị
 * tạo lại. Đặt vị trí theo hình chiếu như DIM module (xem DimensionPlacement).
 *
 * @param {{instanceId: string, part: string, group: THREE.Group,
 *          meshes: THREE.Mesh[]}} target - một mục của buildPartIndex().targets
 * @param {{camera?: THREE.Camera, viewport?: {width, height},
 *          obstacles?: THREE.Group[], offsetM?: number}} opts
 */
export const createPartAutoDimensions = (target, opts = {}) => {
  const { instanceId, part, group, meshes } = target;
  const { camera, viewport, offsetM = DEFAULT_OFFSET_M } = opts;
  const box = getPartBox(group, meshes);
  const axes = PART_AXES[part];
  if (!box || !axes) return [];
  const autoFor = `${instanceId}:${part}`;
  const make = (axis, anchors, offsetAxis, offsetSign, offset) =>
    createDimension({
      group: DIM_GROUP.DETAIL,
      autoFor,
      axis,
      anchors: anchors.map(key => ({ instanceId, part, key })),
      offsetAxis,
      offsetSign,
      offsetM: offset,
    });

  if (camera && viewport) {
    const placements = chooseAutoPlacements(
      group,
      box,
      camera,
      viewport.width,
      viewport.height,
      { obstacles: opts.obstacles, kinds: axes, meshes }
    );
    if (placements?.length && placements.every(Boolean)) {
      return placements.map(pl =>
        make(
          pl.axis,
          pl.anchors.map(a => a.key),
          pl.offsetAxis,
          pl.offsetSign,
          pl.offsetM
        )
      );
    }
  }

  // Không có camera (hoặc bộ phận nằm sau camera): bố cục cố định
  const key = (x, y, z) => buildAnchorKey({ x, y, z });
  const fixed = {
    [DIM_AXIS.WIDTH]: [
      [key('max', 'max', 'min'), key('max', 'max', 'max')],
      'y',
      1,
    ],
    [DIM_AXIS.DEPTH]: [
      [key('min', 'max', 'max'), key('max', 'max', 'max')],
      'z',
      1,
    ],
    [DIM_AXIS.HEIGHT]: [
      [key('max', 'min', 'max'), key('max', 'max', 'max')],
      'x',
      1,
    ],
  };
  return axes.map(axis =>
    make(axis, fixed[axis][0], fixed[axis][1], fixed[axis][2], offsetM)
  );
};

/**
 * Chuyển DIM (dữ liệu lưu trữ) thành điểm world + giá trị đo.
 *
 * Đường DIM song song `axisDir`, nằm cách điểm nhô ra nhất (theo hướng đẩy) một
 * đoạn offsetM; hai đường dóng P1->q1, P2->q2 cùng song song hướng đẩy. Giá trị đo = |(P2 - P1) · axisDir|,
 * không phụ thuộc pixel hay AABB.
 *  - axis width/height/depth: axisDir = trục local tương ứng của module
 *  - axis custom: axisDir = hướng P1 -> P2 (đo thẳng giữa hai điểm)
 * Phương đẩy đường DIM ra: trục local `offsetAxis` (x|y|z), hoặc vector local
 * `offsetDirLocal` khi `offsetAxis === 'custom'` (DIM thủ công).
 *
 * @param {object} dim
 * @param {(instanceId: string) => THREE.Group|null} getGroup
 * @param {object} [opts] - truyền xuống getLocalFrameBox ({now, ttl, frameFn})
 * @returns {null | {id, p1, p2, q1, q2, valueM, axisDir, offsetDir}}
 *          null nếu mất anchor (module đã bị xoá) hoặc hai điểm trùng nhau
 *          trong chế độ đo thẳng.
 */
export const resolveDimension = (dim, getGroup, opts) => {
  if (!dim?.anchors || dim.anchors.length < 2) return null;
  const points = [];
  let firstGroup = null;
  for (const a of dim.anchors.slice(0, 2)) {
    const group = getGroup(a.instanceId);
    if (!group) return null;
    firstGroup = firstGroup ?? group;
    const box = boxForAnchor(group, a, opts);
    const p = resolveAnchorWorld(group, box, a);
    if (!p) return null;
    points.push(p);
  }
  const [p1, p2] = points;
  const delta = p2.clone().sub(p1);

  let axisDir;
  if (dim.axis === DIM_AXIS.CUSTOM) {
    if (delta.lengthSq() < 1e-12) return null;
    axisDir = delta.clone().normalize();
  } else {
    axisDir = localAxisWorldDir(firstGroup, DIM_AXIS_TO_LOCAL[dim.axis] ?? 'z');
  }

  const offsetDir =
    dim.offsetAxis === 'custom' && Array.isArray(dim.offsetDirLocal)
      ? localDirToWorld(firstGroup, dim.offsetDirLocal)
      : localAxisWorldDir(firstGroup, dim.offsetAxis ?? 'y');
  offsetDir.multiplyScalar(dim.offsetSign ?? 1);

  const signed = delta.dot(axisDir);
  // Đường DIM nằm cách điểm "nhô ra nhất" theo hướng đẩy một đoạn offsetM, nên
  // hai đường dóng song song nhau (kể cả khi hai điểm lệch nhau theo hướng đẩy).
  const off = dim.offsetM ?? 0;
  const d1 = p1.dot(offsetDir);
  const d2 = p2.dot(offsetDir);
  const level = (off >= 0 ? Math.max(d1, d2) : Math.min(d1, d2)) + off;
  const q1 = p1.clone().addScaledVector(offsetDir, level - d1);
  return {
    id: dim.id,
    p1,
    p2,
    q1,
    q2: q1.clone().addScaledVector(axisDir, signed),
    valueM: Math.abs(signed),
    axisDir,
    offsetDir,
  };
};

/**
 * Điểm xa nhất của các mesh đang hiện trong module theo hướng `dir` (world),
 * tính trên bbox từng mesh. -Infinity nếu module không có mesh.
 */
export const worldExtentAlong = (group, dir, extraMeshes = []) => {
  group.updateWorldMatrix(true, true);
  let max = -Infinity;
  const v = new THREE.Vector3();
  const visit = o => {
    if (!o.isMesh || !o.geometry) return;
    for (let p = o; p; p = p.parent) if (p.visible === false) return;
    if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
    const { min, max: bmax } = o.geometry.boundingBox;
    if (o.geometry.boundingBox.isEmpty()) return;
    o.updateWorldMatrix(true, false);
    for (const x of [min.x, bmax.x]) {
      for (const y of [min.y, bmax.y]) {
        for (const z of [min.z, bmax.z]) {
          v.set(x, y, z).applyMatrix4(o.matrixWorld);
          max = Math.max(max, v.dot(dir));
        }
      }
    }
  };
  group.traverse(visit);
  extraMeshes.forEach(visit);
  return max;
};

/** Trục local lớn nhất của vector `dirLocal` và tỉ lệ của nó (cos so với hướng đó). */
const dominantLocalAxis = dirLocal => {
  const [x, y, z] = dirLocal.map(Math.abs);
  const len = Math.hypot(x, y, z) || 1;
  const max = Math.max(x, y, z);
  const axis = max === x ? 'x' : max === y ? 'y' : 'z';
  return { axis, cos: max / len };
};

const LOCAL_AXIS_TO_DIM_AXIS = {
  z: DIM_AXIS.WIDTH,
  y: DIM_AXIS.HEIGHT,
  x: DIM_AXIS.DEPTH,
};

/** Ngưỡng cos để chế độ "auto" coi hai điểm là nằm dọc một trục của module. */
const AUTO_AXIS_MIN_COS = 0.9;

/**
 * Tạo một DIM thủ công giữa hai điểm neo.
 *
 * Chế độ trục (`axisMode`):
 *  - 'auto'    : nếu P1->P2 gần song song (cos >= 0.9) với một trục local của
 *                module thì khoá theo trục đó, ngược lại đo thẳng (custom)
 *  - 'width' | 'height' | 'depth': khoá theo trục local tương ứng
 *  - 'aligned' : đo thẳng giữa hai điểm
 * Phương đẩy đường DIM chọn một lần lúc tạo rồi lưu theo hệ local của module
 * (bám theo khi module xoay):
 *  - khoá trục: một trục của module vuông góc trục đo, hướng ra ngoài thân
 *  - đo thẳng: vuông góc phương đo, trong mặt phẳng màn hình, hướng lên trên
 * Mặc định (`clearBody`) đường DIM nằm ngay ngoài thân module + `offsetM`.
 *
 * @returns {object|null} DIM hoặc null nếu hai điểm trùng nhau / thiếu module
 */
export const createManualDimension = ({
  anchors,
  getGroup,
  camera,
  axisMode = 'auto',
  offsetM = DEFAULT_OFFSET_M,
  chainId,
  offsetDirLocal: forcedOffsetDirLocal,
  clearBody = true,
  getPartMeshes,
}) => {
  if (!anchors || anchors.length < 2) return null;
  const groups = anchors.map(a => getGroup(a.instanceId));
  if (groups.some(g => !g)) return null;
  const group0 = groups[0];
  const pts = anchors.map((a, i) =>
    resolveAnchorWorld(
      groups[i],
      boxForAnchor(groups[i], a, { getPartMeshes }),
      a
    )
  );
  if (pts.some(p => !p)) return null;
  const delta = pts[1].clone().sub(pts[0]);
  if (delta.length() < 1e-6) return null;

  // 1) Phương đo
  const deltaLocal = worldDirToLocal(group0, delta);
  let axis;
  if (['width', 'height', 'depth'].includes(axisMode)) {
    axis = axisMode;
  } else if (axisMode === 'aligned') {
    axis = DIM_AXIS.CUSTOM;
  } else {
    const dom = dominantLocalAxis(deltaLocal);
    axis =
      dom.cos >= AUTO_AXIS_MIN_COS
        ? LOCAL_AXIS_TO_DIM_AXIS[dom.axis]
        : DIM_AXIS.CUSTOM;
  }
  const axisDir =
    axis === DIM_AXIS.CUSTOM
      ? delta.clone().normalize()
      : localAxisWorldDir(group0, DIM_AXIS_TO_LOCAL[axis]);

  // Thân = các module + các bộ phận (mặt đá, ốp tường) mà điểm đo thuộc về
  const partMeshes = anchors.flatMap(a =>
    a.part ? (getPartMeshes?.(a.instanceId, a.part) ?? []) : []
  );
  const uniqueGroups = [...new Set(groups)];

  // 2) Phương đẩy đường DIM ra (vuông góc phương đo)
  let offsetDirLocal = forcedOffsetDirLocal;
  if (!offsetDirLocal) {
    let dir;
    if (axis === DIM_AXIS.CUSTOM) {
      dir = pickOffsetDirection(axisDir, pts, camera);
    } else {
      // Ưu tiên hướng cho đường DIM nằm NGOÀI hình chiếu của tủ trên màn hình
      // (không đè lên cánh tủ); không tính được thì dùng quy tắc theo trục
      dir =
        pickOffsetByScreenOverlap({
          groups: uniqueGroups,
          group0,
          axisLocal: DIM_AXIS_TO_LOCAL[axis],
          axisDir,
          delta,
          pts,
          camera,
          partMeshes,
        }) ??
        pickAxisLockedOffset({
          group: group0,
          axisLocal: DIM_AXIS_TO_LOCAL[axis],
          axisDir,
          delta,
          pts,
          camera,
        });
    }
    offsetDirLocal = worldDirToLocal(group0, dir);
  }

  // 3) Mặc định đặt đường DIM ngay NGOÀI thân module theo hướng đẩy (như bản vẽ
  //    kỹ thuật), cách thân `offsetM`. Các đoạn của một chuỗi vì vậy thẳng hàng.
  let finalOffsetM = offsetM;
  if (clearBody && offsetM >= 0) {
    const n = localDirToWorld(group0, offsetDirLocal);
    const extent = Math.max(
      ...uniqueGroups.map(g => worldExtentAlong(g, n, partMeshes))
    );
    const outermost = Math.max(pts[0].dot(n), pts[1].dot(n));
    if (Number.isFinite(extent) && extent > outermost) {
      finalOffsetM = extent - outermost + offsetM;
    }
  }

  return createDimension({
    anchors: anchors.map(a => ({ ...a })),
    axis,
    offsetAxis: 'custom',
    offsetDirLocal,
    offsetSign: 1,
    offsetM: finalOffsetM,
    group: DIM_GROUP.DETAIL,
    ...(chainId ? { chainId } : {}),
  });
};

/**
 * Phương đẩy cho DIM khoá trục (rộng / cao / sâu): một trục local của module
 * vuông góc với trục đo, giống bản vẽ kỹ thuật.
 *  - ưu tiên trục hút hết phần chênh lệch của hai điểm ngoài trục đo, để hai
 *    đường dóng song song (vd. hai điểm lệch độ cao => đẩy theo trục đứng)
 *  - loại trục gần như trùng hướng với trục đo trên màn hình (DIM sẽ bẹp)
 *  - chiều: hướng ra ngoài thân module; không rõ thì hướng lên trên màn hình
 */
/** Khung nhìn ảo (px) để chấm điểm vị trí; chỉ cần đúng tỉ lệ khung hình. */
const SCREEN_HEIGHT = 1000;

/**
 * Chọn phương đẩy cho DIM khoá trục theo HÌNH CHIẾU trên màn hình: thử cả hai
 * trục vuông góc với trục đo, mỗi trục hai chiều, đặt đường DIM ngay ngoài thân
 * (như bước 3 của createManualDimension) rồi chấm điểm bằng cùng bộ chấm điểm
 * của Auto DIM (đè lên thân tủ, bị co ngắn, ra ngoài khung). Nhờ vậy DIM đo cạnh
 * trước của mặt đá nằm phía trên mặt đá chứ không đè lên mặt cánh tủ.
 *
 * @returns {THREE.Vector3|null} null khi không chiếu được (vd. nằm sau camera)
 */
const pickOffsetByScreenOverlap = ({
  groups,
  group0,
  axisLocal,
  axisDir,
  delta,
  pts,
  camera,
  partMeshes,
}) => {
  if (!camera) return null;
  const width = (camera.aspect || 1) * SCREEN_HEIGHT;
  const height = SCREEN_HEIGHT;
  const meshes = [...partMeshes];
  groups.forEach(g =>
    g.traverse(o => {
      if (o.isMesh) meshes.push(o);
    })
  );
  const hull = silhouetteOfMeshes(meshes, camera, width, height);
  if (!hull) return null;

  const length = delta.length() || 1;
  const perps = ['x', 'y', 'z'].filter(a => a !== axisLocal);
  let best = null;
  perps.forEach((name, i) => {
    const base = localAxisWorldDir(group0, name);
    const other = localAxisWorldDir(group0, perps[1 - i]);
    // Hai điểm lệch nhau theo trục còn lại thì ưu tiên đẩy theo trục kia
    const residual = Math.abs(delta.dot(other)) / length;
    [1, -1].forEach(sign => {
      const n = base.clone().multiplyScalar(sign);
      const outermost = Math.max(pts[0].dot(n), pts[1].dot(n));
      const extent = Math.max(
        ...groups.map(g => worldExtentAlong(g, n, partMeshes))
      );
      const level =
        Math.max(outermost, Number.isFinite(extent) ? extent : outermost) +
        DEFAULT_OFFSET_M;
      const q1 = pts[0].clone().addScaledVector(n, level - pts[0].dot(n));
      const q2 = q1.clone().addScaledVector(axisDir, delta.dot(axisDir));
      const proj = [pts[0], pts[1], q1, q2].map(p =>
        projectToScreen(p, camera, width, height)
      );
      if (proj.some(p => !p.visible)) return;
      const { score } = scoreCandidate(
        { p1: proj[0], p2: proj[1], q1: proj[2], q2: proj[3] },
        hull,
        width,
        height
      );
      // Hoà điểm: ưu tiên hướng thẳng đứng như Auto DIM (lên trên, rồi xuống dưới)
      // hơn hướng ngang (không để đường DIM chạy ra phía sau sát tường)
      const bias = name === 'y' ? (sign === 1 ? 0 : 1) : 4;
      // Không đặt đường DIM ở phía sau tủ (xa camera hơn thân tủ)
      const behind = pushesAway(n, pts[0], pts[1], camera)
        ? HIDDEN_SIDE_PENALTY
        : 0;
      const total = score + residual * 20 + bias + behind;
      if (!best || total < best.total) best = { total, n };
    });
  });
  return best ? best.n : null;
};

const pickAxisLockedOffset = ({
  group,
  axisLocal,
  axisDir,
  delta,
  pts,
  camera,
}) => {
  const mid = pts[0].clone().add(pts[1]).multiplyScalar(0.5);
  const box = getLocalFrameBox(group);
  group.updateWorldMatrix(true, false);
  const center = box
    ? box.getCenter(new THREE.Vector3()).applyMatrix4(group.matrixWorld)
    : new THREE.Vector3().setFromMatrixPosition(group.matrixWorld);
  const length = delta.length() || 1;

  const toScreen = p => {
    if (!camera) return { x: p.x, y: -p.y };
    const v = p.clone().project(camera);
    return { x: v.x, y: -v.y };
  };
  const screenDir = dir => {
    const a = toScreen(mid);
    const b = toScreen(mid.clone().addScaledVector(dir, 0.1));
    return { x: b.x - a.x, y: b.y - a.y };
  };
  const axisScreen = screenDir(axisDir);

  const perps = ['x', 'y', 'z'].filter(a => a !== axisLocal);
  let best = null;
  perps.forEach((name, i) => {
    const n = localAxisWorldDir(group, name);
    const other = localAxisWorldDir(group, perps[1 - i]);
    const residual = Math.abs(delta.dot(other)) / length;
    const ns = screenDir(n);
    const cross = Math.abs(axisScreen.x * ns.y - axisScreen.y * ns.x);
    const norms =
      Math.hypot(axisScreen.x, axisScreen.y) * Math.hypot(ns.x, ns.y);
    const sin = norms > 1e-12 ? cross / norms : 0;
    const score = residual + (sin < 0.3 ? 1 : 0) - sin * 0.01;
    if (!best || score < best.score) best = { score, n, ns };
  });

  const { n, ns } = best;
  const outward = mid.clone().sub(center).dot(n);
  let sign;
  if (Math.abs(outward) > 0.01) sign = Math.sign(outward);
  else sign = ns.y <= 0 ? 1 : -1; // hướng lên trên màn hình
  return n.multiplyScalar(sign);
};

/**
 * Phương đẩy cho DIM đo thẳng: vuông góc với `axisDir` và với hướng nhìn (nên nằm trong
 * mặt phẳng màn hình), chiều hướng lên trên màn hình.
 */
const pickOffsetDirection = (axisDir, points, camera) => {
  const mid = points[0].clone().add(points[1]).multiplyScalar(0.5);
  const up = new THREE.Vector3(0, 1, 0);
  const right = new THREE.Vector3(1, 0, 0);
  let view = new THREE.Vector3(0, 0, -1);
  if (camera) {
    camera.updateMatrixWorld?.(true);
    up.setFromMatrixColumn(camera.matrixWorld, 1);
    right.setFromMatrixColumn(camera.matrixWorld, 0);
    view = mid.clone().sub(camera.position).normalize();
  }
  let n = new THREE.Vector3().crossVectors(view, axisDir);
  if (n.lengthSq() < 1e-8) {
    // Phương đo song song hướng nhìn: lấy phương bất kỳ vuông góc
    n = new THREE.Vector3().crossVectors(up, axisDir);
    if (n.lengthSq() < 1e-8)
      n = new THREE.Vector3().crossVectors(right, axisDir);
  }
  n.normalize();
  const upDot = n.dot(up);
  // Nếu n gần ngang (phương đo thẳng đứng) thì ưu tiên hướng sang phải
  const sign =
    Math.abs(upDot) > 0.2 ? Math.sign(upDot) : Math.sign(n.dot(right)) || 1;
  return n.multiplyScalar(sign);
};
